import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { CreditCard, Folder, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDropdownStore, type ActiveDropdownId } from "@/stores/useDropdownStore";
import type { DeckInitialType } from "./DeckModal";

type Placement = "bottom" | "top" | "right" | "left";

interface NewDeckDropdownProps {
  onSelect: (type: DeckInitialType) => void;
  /** When provided (e.g. from Sidebar), show simplified "Card Deck + Folder" options instead of words/cards/tests */
  onCreateFolder?: () => void;
  /** Compact trigger (e.g. + icon only) vs full button label vs icon-only circular */
  variant?: "compact" | "full" | "icon";
  /** Translation key for trigger label (default: sidebar.newDeck) */
  triggerLabelKey?: string;
  /** Additional class for the trigger container (e.g. for dashed card) */
  className?: string;
  /** Inline styles for the trigger when used as a card */
  triggerStyle?: React.CSSProperties;
  /** When true, dropdown opens upwards (above the button) - use for bottom-positioned triggers */
  openUpwards?: boolean;
  /** Horizontal alignment of dropdown menu: "left" anchors to trigger's left edge; "center" (default) centers it; "end" anchors to trigger's right edge (e.g. header top-right to avoid overflow) */
  menuAlign?: "left" | "center" | "end";
  /** Placement of dropdown relative to trigger. "right" for narrow sidebars to avoid overlap */
  placement?: Placement;
  /** When set, only show this deck type in the dropdown (e.g. Library tab filtering) */
  restrictToType?: DeckInitialType;
  /** Shared dropdown store id so multiple "+" menus can close each other cleanly. */
  dropdownId?: ActiveDropdownId;
}

const OPTIONS: DeckInitialType[] = ["words", "cards", "tests"];

const OPTION_EMOJIS: Record<DeckInitialType, string> = {
  words: "🔤",
  cards: "🃏",
  tests: "📝",
};

const TOUCH_CLICK_SUPPRESS_MS = 700;

export default function NewDeckDropdown({
  onSelect,
  onCreateFolder,
  variant = "full",
  triggerLabelKey = "sidebar.newDeck",
  className,
  triggerStyle,
  openUpwards = false,
  menuAlign = "center",
  placement = "bottom",
  restrictToType,
  dropdownId = "new-deck",
}: NewDeckDropdownProps) {
  const { t } = useTranslation();
  const activeDropdownId = useDropdownStore((s) => s.activeDropdownId);
  const setActiveDropdownId = useDropdownStore((s) => s.setActiveDropdownId);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const lastTouchActionRef = useRef(0);
  const [portalPosition, setPortalPosition] = useState<{ top: number; left: number; transform: string } | null>(null);

  // Close when another dropdown (e.g. Header +) opens
  useEffect(() => {
    if (activeDropdownId !== null && activeDropdownId !== dropdownId) {
      setOpen(false);
    }
  }, [activeDropdownId, dropdownId]);

  const effectivePlacement: Placement =
    openUpwards && placement === "bottom" ? "top" : placement;
  const usePortal = effectivePlacement === "right" || effectivePlacement === "left";

  useLayoutEffect(() => {
    if (!open || !usePortal || !buttonRef.current) {
      if (!open) setPortalPosition(null);
      return;
    }
    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const gap = 10;
      if (effectivePlacement === "right") {
        setPortalPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + gap,
          transform: "translateY(-50%)",
        });
      } else {
        setPortalPosition({
          top: rect.top + rect.height / 2,
          left: rect.left - gap,
          transform: "translate(-100%, -50%)",
        });
      }
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, usePortal, effectivePlacement]);

  useEffect(() => {
    const handlePointerDownOutside = (e: PointerEvent) => {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inContainer && !inDropdown) {
        setOpen(false);
        setActiveDropdownId(null);
      }
    };
    document.addEventListener("pointerdown", handlePointerDownOutside);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutside);
  }, [setActiveDropdownId]);

  const handleOptionClick = (type: DeckInitialType) => {
    onSelect(type);
    setOpen(false);
    setActiveDropdownId(null);
  };

  const handleCreateFolder = () => {
    onCreateFolder?.();
    setOpen(false);
    setActiveDropdownId(null);
  };

  const useSimplifiedMenu = !!onCreateFolder && !restrictToType;

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    setActiveDropdownId(next ? dropdownId : null);
  };

  const runTouchAction = (action: () => void) => (event: React.TouchEvent<HTMLElement>) => {
    if (Date.now() - lastTouchActionRef.current < TOUCH_CLICK_SUPPRESS_MS) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    lastTouchActionRef.current = Date.now();
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  const runPointerAction = (action: () => void) => (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse") return;
    lastTouchActionRef.current = Date.now();
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  const runClickAction = (action: () => void) => (event: React.MouseEvent<HTMLElement>) => {
    if (Date.now() - lastTouchActionRef.current < TOUCH_CLICK_SUPPRESS_MS) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    event.stopPropagation();
    action();
  };

  const triggerLabel = variant === "full" ? t(triggerLabelKey) : "+";

  const baseTriggerStyle: React.CSSProperties =
    variant === "icon"
      ? {
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "none",
          background: "rgb(99, 102, 241)",
          color: "white",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background .2s",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)",
        }
      : variant === "full"
      ? {
          width: "100%",
          padding: "10px 16px",
          borderRadius: 50,
          border: "none",
          background: "hsl(var(--primary))",
          color: "white",
          fontFamily: "DM Sans, sans-serif",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          transition: "all .2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }
      : {
          background: "transparent",
          border: "none",
          color: "hsl(var(--primary))",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
          padding: "0 4px",
          display: "flex",
          alignItems: "center",
          gap: 4,
        };

  const cardTriggerStyle: React.CSSProperties =
    variant === "compact" && triggerStyle
      ? {
          ...baseTriggerStyle,
          width: "100%",
          height: "100%",
          background: "transparent",
          color: "inherit",
          fontSize: 32,
          flexDirection: "column",
          gap: 8,
        }
      : baseTriggerStyle;

  const dropdownAlignStyle: React.CSSProperties =
    variant === "full" && !triggerStyle?.width
      ? { left: 0, right: 0 }
      : menuAlign === "left"
        ? { left: 0 }
        : menuAlign === "end"
          ? { right: 0, left: "auto" }
          : { left: "50%", transform: "translateX(-50%)" };

  const getDropdownPosition = (): React.CSSProperties => {
    const gap = 8;
    const base = { zIndex: 9999 };

    switch (effectivePlacement) {
      case "right":
        return {
          ...base,
          left: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginLeft: gap,
          marginTop: 0,
          marginBottom: 0,
        };
      case "left":
        return {
          ...base,
          right: "100%",
          top: "50%",
          transform: "translateY(-50%)",
          marginRight: gap,
          marginTop: 0,
          marginBottom: 0,
        };
      case "top":
        return {
          ...base,
          bottom: "100%",
          marginBottom: gap,
          marginTop: 0,
          ...dropdownAlignStyle,
        };
      case "bottom":
      default:
        return {
          ...base,
          top: "100%",
          marginTop: gap,
          marginBottom: 0,
          ...dropdownAlignStyle,
        };
    }
  };

  const optionsToShow: DeckInitialType[] = restrictToType
    ? OPTIONS.filter((t) => t === restrictToType)
    : OPTIONS;

  const dropdownContent = useSimplifiedMenu ? (
    <div
      ref={(el) => { dropdownRef.current = el; }}
      role="menu"
      aria-label={t("sidebar.newDeck")}
      className={`flex flex-col gap-2 rounded-xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 shadow-xl z-[9999] ${variant === "full" && !triggerStyle?.width ? "w-full" : "min-w-[220px]"}`}
      style={
        usePortal && portalPosition
          ? {
              position: "fixed",
              top: portalPosition.top,
              left: portalPosition.left,
              transform: portalPosition.transform,
              zIndex: 9999,
            }
          : {
              position: "absolute",
              ...getDropdownPosition(),
            }
      }
    >
      <button
        type="button"
        role="menuitem"
        onPointerUp={runPointerAction(() => handleOptionClick("cards"))}
        onClick={runClickAction(() => handleOptionClick("cards"))}
        onTouchEnd={runTouchAction(() => handleOptionClick("cards"))}
        className="flex min-w-0 w-full items-center gap-2 rounded-xl p-2.5 text-sm font-medium whitespace-normal text-left leading-tight text-slate-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800/60 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors duration-200 cursor-pointer"
        style={{ border: "none", fontFamily: "DM Sans, sans-serif", touchAction: "manipulation" }}
      >
        <CreditCard size={18} className="shrink-0" />
        <span className="break-words">{t("buttons.cardDeck")}</span>
      </button>
      <button
        type="button"
        role="menuitem"
        onPointerUp={runPointerAction(handleCreateFolder)}
        onClick={runClickAction(handleCreateFolder)}
        onTouchEnd={runTouchAction(handleCreateFolder)}
        className="flex min-w-0 w-full items-center gap-2 rounded-xl p-2.5 text-sm font-medium whitespace-normal text-left leading-tight text-slate-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800/60 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors duration-200 cursor-pointer"
        style={{ border: "none", fontFamily: "DM Sans, sans-serif", touchAction: "manipulation" }}
      >
        <Folder size={18} className="shrink-0" />
        <span className="break-words">{t("buttons.folder")}</span>
      </button>
    </div>
  ) : (
    <div
      ref={(el) => { dropdownRef.current = el; }}
      role="menu"
      aria-label={t("sidebar.newDeck")}
      className={`flex flex-col gap-2 rounded-xl overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 shadow-xl z-[9999] ${variant === "full" && !triggerStyle?.width ? "w-full" : "min-w-[220px]"}`}
      style={
        usePortal && portalPosition
          ? {
              position: "fixed",
              top: portalPosition.top,
              left: portalPosition.left,
              transform: portalPosition.transform,
              zIndex: 9999,
            }
          : {
              position: "absolute",
              ...getDropdownPosition(),
            }
      }
    >
      {optionsToShow.map((type) => (
        <button
          key={type}
          type="button"
          role="menuitem"
          onPointerUp={runPointerAction(() => handleOptionClick(type))}
          onClick={runClickAction(() => handleOptionClick(type))}
          onTouchEnd={runTouchAction(() => handleOptionClick(type))}
          className="flex min-w-0 w-full items-center gap-2 rounded-xl p-2.5 text-sm font-medium whitespace-normal text-left leading-tight text-slate-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-800/60 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors duration-200 cursor-pointer"
          style={{
            border: "none",
            fontFamily: "DM Sans, sans-serif",
            touchAction: "manipulation",
          }}
        >
          <span aria-hidden className="shrink-0 text-base">{OPTION_EMOJIS[type]}</span>
          <span className="break-words">{t(`buttons.create${type.charAt(0).toUpperCase() + type.slice(1)}Set`)}</span>
        </button>
      ))}
    </div>
  );

  const iconTriggerStyle =
    variant === "icon"
      ? { ...baseTriggerStyle, background: hovered ? "rgb(79, 70, 229)" : baseTriggerStyle.background }
      : {};

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block", ...(variant === "full" && !triggerStyle?.width ? { width: "100%" } : {}) }} className={className}>
      <button
        ref={buttonRef}
        type="button"
        onPointerUp={runPointerAction(handleToggle)}
        onClick={runClickAction(handleToggle)}
        onTouchEnd={runTouchAction(handleToggle)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          ...cardTriggerStyle,
          ...triggerStyle,
          ...iconTriggerStyle,
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",
          pointerEvents: "auto",
        }}
        title={t("sidebar.newDeckTitle")}
      >
        {variant === "compact" && triggerStyle ? (
          <>
            <span style={{ fontSize: 32 }}>+</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{t("sidebar.newDeck")}</span>
          </>
        ) : variant === "icon" ? (
          <Plus size={22} strokeWidth={2.5} />
        ) : (
          <>
            {triggerLabel}
            {variant === "full" && (
              <span
                style={{
                  fontSize: 10,
                  transition: "transform .2s",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            )}
          </>
        )}
      </button>

      {open &&
        (usePortal
          ? portalPosition && createPortal(dropdownContent, document.body)
          : dropdownContent)}
    </div>
  );
}
